/**
 * Datadog RUM — initialized from GET /api/config (public client token + application id).
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/setup/client/
 */

let _initialized = false;
let _initPromise = null;
let _rum = null;

/**
 * @param {object} rum — From /api/config: ddRumApplicationId, ddRumClientToken, ddRumSite, …
 * @param {{ sub?: string, email?: string } | null} user — Auth0 user when available
 */
export function initDatadogRum(rum, user) {
  if (typeof window === "undefined" || _initialized) return _initPromise;
  const appId = (rum?.ddRumApplicationId || "").trim();
  const clientToken = (rum?.ddRumClientToken || "").trim();
  if (!appId || !clientToken) return undefined;

  if (_initPromise) return _initPromise;

  const site = (rum?.ddRumSite || "datadoghq.com").trim();
  const service = (rum?.ddRumService || "explorer").trim();
  const env = (rum?.ddRumEnv || "").trim();
  const version = (rum?.ddRumVersion || "").trim();
  const parseRate = (v, fallback) => {
    if (v === "" || v == null) return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const sessionSampleRate = parseRate(rum?.ddRumSessionSampleRate, 100);
  const replaySampleRate = parseRate(rum?.ddRumSessionReplaySampleRate, 20);

  _initPromise = import("@datadog/browser-rum")
    .then((mod) => {
      _rum = mod.datadogRum;
      _rum.init({
        applicationId: appId,
        clientToken,
        site,
        service,
        env: env || undefined,
        version: version || undefined,
        sessionSampleRate: Number.isFinite(sessionSampleRate)
          ? Math.min(100, Math.max(0, sessionSampleRate))
          : 100,
        sessionReplaySampleRate: Number.isFinite(replaySampleRate)
          ? Math.min(100, Math.max(0, replaySampleRate))
          : 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
      });
      _initialized = true;
      if (user?.sub) {
        _rum.setUser({
          id: user.sub,
          email: user.email,
        });
      }
    })
    .catch((err) => {
      _initPromise = null;
      console.warn("[Datadog RUM] init failed:", err);
    });

  return _initPromise;
}

/** Stops the current RUM session (e.g. @murmuration.org users). */
export function stopDatadogRumSession() {
  if (!_rum || !_initialized) return;
  try {
    _rum.stopSession();
  } catch (_) {
    /* ignore */
  }
}

export function setDatadogRumUser(user) {
  if (!_initPromise || typeof window === "undefined") return;
  _initPromise.then(() => {
    if (!_initialized || !_rum) return;
    if (user?.sub) {
      _rum.setUser({ id: user.sub, email: user.email });
    } else {
      _rum.clearUser();
    }
  });
}
